import { Box, Button, Typography } from '@mui/material';
import Divider from 'components/divider';
import IconButton from 'components/icon-button';
import NumberField from 'components/number-field';
import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { Info, TimerReset } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Prompt } from 'react-router-dom';
import { getEnumKeysAsArray } from 'utils/enum';
import titleCase from 'utils/title-case';
import { putStepThree } from 'views/playground/features/scenario-planner-2.0/api/update-step-three';
import { useScenarioPlanState } from 'views/playground/features/scenario-planner-2.0/context/scenario-plan-context';
import { ScenarioPlan } from 'views/playground/features/scenario-planner-2.0/types/scenario-plan.type';
import { StepProps } from 'views/playground/features/scenario-planner-2.0/types/step-props.type';
import { UpdateStepThreeInput } from 'views/playground/features/scenario-planner-2.0/types/update-step-three-input.type';
import { Currency } from 'views/bluviu-nutrition/features/scenarios/types/scenario.type';

const StepThree: React.FC<StepProps> = ({ onClick }) => {
  const {
    scenarioPlanQuery: { data },
  } = useScenarioPlanState();
  const queryClient = useQueryClient();

  const defaultValues = useMemo(() => {
    if (!!data?.stepThreeFields) {
      return data.stepThreeFields;
    }

    return {
      treatment: '',
      cost: '',
      currency: Currency.NOK,
      productCost: '',
      harvestCost: '',
      smoltCost: '',
    };
  }, [data]);

  const { control, handleSubmit, formState } = useForm<UpdateStepThreeInput>({
    defaultValues: defaultValues, // step default form values
  });

  const snackbarDispatch = useSnackbarDispatch();

  const updateStepOne = usePost<ScenarioPlan, UpdateStepThreeInput>(
    (request) => putStepThree(request),
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Success!' },
      });

      queryClient.invalidateQueries({
        queryKey: ['getPlaygroundScenarioPlan']
      });
    },
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Fail!' },
      });
    }
  );

  const handleUpdate = (data: UpdateStepThreeInput) => {
    updateStepOne.mutate(data, {
      onSuccess: () => {
        onClick(3); // only pass if successful update
      },
    });
  };

  const isFormDirty = useMemo(() => formState.isDirty, [formState]);

  const TreatmentsSection = () => {
    return (
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography>Treatments</Typography>
        <Box display={'flex'} flexDirection={'row'} gap={4}>
          <TextField
            label={'Treatment'}
            dark
            fullWidth
            controller={{
              control,
              name: 'treatment',
              rules: {
                required: true,
              },
            }}
          />
          <TextField
            label={'Cost'}
            dark
            fullWidth
            controller={{
              control,
              name: 'cost',
              rules: {
                required: true,
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  const ResetSection = () => {
    return (
      <Box display={'flex'} flexDirection={'row'} gap={4} justifyContent={'space-between'}>
        <Typography display={'flex'} justifyContent={'center'} gap={2}>
          <Info /> Using site defaults as input
        </Typography>
        <IconButton disabled title="Reset to defaults">
          <TimerReset size={22} />
        </IconButton>
      </Box>
    );
  };

  const ProductionCostSection = () => {
    return (
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography>Production costs</Typography>
        <Box display={'flex'} gap={4}>
          <NumberField
            label={'Product cost / week'}
            dark
            fullWidth
            endAdornment={'/ week per cage'}
            controller={{
              control,
              name: 'productCost',
              rules: {
                required: true,
              },
            }}
          />
          <NumberField
            label={'Harvest cost / kg'}
            dark
            fullWidth
            endAdornment={'/ kg harvest'}
            controller={{
              control,
              name: 'harvestCost',
              rules: {
                required: true,
              },
            }}
          />
        </Box>
        <Box display={'flex'} flexDirection={'row'} gap={4}>
          <NumberField
            label={'Smolt cost (kg)'}
            dark
            fullWidth
            endAdornment={'/ kg'}
            controller={{
              control,
              name: 'smoltCostKg',
              rules: {
                required: true,
              },
            }}
          />
          <NumberField
            label={'Smolt cost / smolt'}
            dark
            fullWidth
            endAdornment={'/ smolt'}
            controller={{
              control,
              name: 'smoltCost',
              rules: {
                required: true,
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Prompt when={isFormDirty} message="You have unsaved changes. Are you sure you want to leave this page?" />
      <Paper
        sx={{
          height: '100%',
        }}>
        <form
          onSubmit={handleSubmit(handleUpdate)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
          <Box display={'flex'} flexDirection={'column'} flexGrow={1} gap={4}>
            <TreatmentsSection />
            <Divider flexItem dark />
            <ResetSection />
            <Select
              label={'Currency'}
              dark
              fullWidth
              controller={{
                control,
                name: 'currency',
                rules: {
                  required: true,
                },
              }}>
              {getEnumKeysAsArray(Currency).map((currency) => (
                <RadioSelectItem key={currency} value={currency}>
                  {titleCase(currency)}
                </RadioSelectItem>
              ))}
            </Select>
            <ProductionCostSection />
            <Box display={'flex'} flexDirection={'row'} gap={2} justifyContent={'flex-end'}>
              <Button onClick={() => onClick(1)}>Back</Button>
              <Button type="submit">Next</Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </>
  );
};

export default StepThree;
